import { createFileRoute, useBlocker } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { api } from "~/utils/api"
import SelectableCard from "~/components/collections/cards/SelectableCard"
import CollectionInfoCard from "~/components/demand-plan/CollectionInfoCard"
import { WorkspacePage } from "~/components/design-system/pages"
import ViewConfiguration from "~/components/planning/ViewConfiguration"
import { COLLECTIONS_DEMAND_TRIM_TAB_TABLE_ID } from "~/constants"
import { useGetCollectionsWithDetails } from "~/hooks/useCollections"
import { difference, flatMap, isEqual, sortBy, sumBy } from "lodash"
import { ProductTrimTable } from "~/components/demand-plan/ProductTrimTable"
import useCollectionComparisonTableData from "~/hooks/useCollectionComparisonTableData"
import { formatNumber } from "~/utils/formatting"
import { useDemandPlanContext, VariantWithMetricsAndAlerts } from "~/context/DemandPlanProvider"
import { useVariants } from "~/hooks/useVariants"
import { useCreateDemandPlanEvent, useUpsertTrimmedVariants } from "~/hooks/useDemandPlan"
import { Button } from "~/components/reusable/Button"
import { DemandPlanEventType } from "~/constants/demandPlanEventType"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import { VariantCountMetricCard } from "~/components/demand-plan/metric-cards/VariantCountMetricCard"
import { PreorderCountMetricCard } from "~/components/demand-plan/metric-cards/PreorderCountMetricCard"
import { SkuCountMetricCard } from "~/components/demand-plan/metric-cards/SkuCountMetricCard"
import { PreorderPerVariantMetricCard } from "~/components/demand-plan/metric-cards/PreorderPerVariantMetricCard"
import { RevenueTotalMetricCard } from "~/components/demand-plan/metric-cards/RevenueTotalMetricCard"
import { RevenuePerVariantMetricCard } from "~/components/demand-plan/metric-cards/RevenuePerVariantMetricCard"
import { TotalForecastedDemandCard } from "~/components/demand-plan/metric-cards/TotalForecastedDemandCard"
import { formatName } from "~/utils/namingUtils"
import { useFilters } from "~/hooks/useFilters"
import { filterVariants } from "~/utils/filter"
import { usePageTracking } from "~/hooks/usePageTracking"
import { isCollectionEnded } from "~/utils/collections"
import { SalesMetricColumns, TrimMetricsColumns } from "~/utils/demandDictionary"
import { columnNaming } from "~/components/planning/ProductTable/utils/columnUtils"

export const Route = createFileRoute("/_auth/demand-planning/fashion/$collectionId/_layout/trim")({
  component: RouteComponent,
})

function RouteComponent() {
  usePageTracking("Collection - Trim")
  /* Contexts / Providers */
  const {
    sourceCollectionIds,
    sourceCollections,
    baselineCollectionIds,
    sourceCollectionName,
    baselineCollectionName,
    demandPlanId,
    allVariants,
    trimmedVariantIds,
    variantsLoading,
  } = useDemandPlanContext()
  const { data: comparisonData } = useGetCollectionsWithDetails({
    collectionIds: baselineCollectionIds ?? [],
  })
  const upsertTrimmedVariants = useUpsertTrimmedVariants()
  const createDemandPlanEvent = useCreateDemandPlanEvent()
  const rawVariants = useVariants()
  const rawVariantIdSet = useMemo(() => new Set(rawVariants.map((v) => v.variantId)), [rawVariants])
  const allComparisonVariantIds = useMemo(() => {
    const allIds = flatMap(comparisonData, (col) => col.variantIds)
    return [...new Set(allIds)].filter((id) => rawVariantIdSet.has(id))
  }, [comparisonData, rawVariantIdSet])

  // Filter comparison variants to only include those not fully out in baseline collections
  const { data: inScopeComparisonVariantIds } = api.collection.getVariantsNotFullyOut.useQuery(
    { variantIds: allComparisonVariantIds, collectionIds: baselineCollectionIds ?? [] },
    { enabled: allComparisonVariantIds.length > 0 && (baselineCollectionIds ?? []).length > 0 },
  )
  const comparisonVariantIds = inScopeComparisonVariantIds ?? []

  const endedBaselineCollectionIds = useMemo(
    () => comparisonData.filter(isCollectionEnded).map((c) => c.id),
    [comparisonData],
  )
  const { data: comparisonVariantData } = useCollectionComparisonTableData({
    variantIds: comparisonVariantIds,
    collectionIds: baselineCollectionIds ?? [],
    endedCollectionIds: endedBaselineCollectionIds,
  })
  /* API Data */

  const { filters } = useFilters()

  const variants = useMemo(() => {
    return filterVariants(filters, allVariants) as VariantWithMetricsAndAlerts[]
  }, [allVariants, filters])

  /* State */
  const [tempTrimmedVariantIds, setTempTrimmedVariants] = useState<string[]>(
    trimmedVariantIds ?? [],
  )

  /* Hooks */
  const unsavedChanges = !isEqual(sortBy(tempTrimmedVariantIds), sortBy(trimmedVariantIds))
  useBlocker({
    shouldBlockFn: () => {
      if (!unsavedChanges) return false

      return !confirm("There are unsaved changes, are you sure you wish to leave?")
    },
  })

  const tempTrimmedSet = new Set(tempTrimmedVariantIds)
  const removedVariants = variants.filter((v) => tempTrimmedSet.has(v.variantId))
  const remainingVariants = variants.filter((v) => !tempTrimmedSet.has(v.variantId))
  const saveTrimChanges = () => {
    if (demandPlanId && unsavedChanges) {
      const newTrimmedVariants = trimmedVariantIds
        ? difference(tempTrimmedVariantIds, trimmedVariantIds)
        : []
      const newUntrimmedVariants = trimmedVariantIds
        ? difference(trimmedVariantIds, tempTrimmedVariantIds)
        : []
      upsertTrimmedVariants.mutate(
        {
          trimmedVariantIds: tempTrimmedVariantIds,
          demandPlanId: demandPlanId,
        },
        {
          onSuccess: () => {
            if (newTrimmedVariants.length > 0) {
              createDemandPlanEvent.mutate({
                demandPlanId,
                variantIds: newTrimmedVariants,
                description: DemandPlanEventType.Trimmed,
              })
            }
            if (newUntrimmedVariants.length > 0) {
              createDemandPlanEvent.mutate({
                demandPlanId,
                variantIds: newUntrimmedVariants,
                description: DemandPlanEventType.Untrimmed,
              })
            }
          },
        },
      )
    }
  }

  const ppv = sumBy(remainingVariants, (v) => v.preOrderQuantitySource) / remainingVariants.length
  const ppvDelta = ppv - sumBy(variants, (v) => v.preOrderQuantitySource) / variants.length
  const rpv = sumBy(remainingVariants, (v) => v.revenueSource) / remainingVariants.length
  const rpvDelta = rpv - sumBy(variants, (v) => v.revenueSource) / variants.length

  const cardDataComponents = [
    {
      title: "Variant count",
      description: "The amount of variants present in the current collection.",
      content: (
        <div className="h-full w-full p-4">
          <VariantCountMetricCard
            variants={remainingVariants}
            baselineVariantCount={comparisonVariantIds.length}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
      footer: <div>Trimmed variants: {formatNumber(removedVariants.length)}</div>,
    },
    {
      title: columnNaming[TrimMetricsColumns.PreorderQuantity].columnTitle,
      content: (
        <div className="h-full w-full p-4">
          <PreorderCountMetricCard
            variants={remainingVariants}
            baselineVariantData={comparisonVariantData}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
      footer: (
        <div>
          Trimmed {formatName("preorder", true)}:{" "}
          {formatNumber(sumBy(removedVariants, (v) => v.preOrderQuantitySource))}
        </div>
      ),
    },
    {
      title: "SKU count",
      content: (
        <div className="h-full w-full p-4">
          <SkuCountMetricCard
            variants={remainingVariants}
            baselineData={comparisonData}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
      footer: (
        <div>Trimmed SKUs: {formatNumber(sumBy(removedVariants, (v) => v.skuIds.length))}</div>
      ),
    },
    {
      title: `${formatName("preorder", true, true)} per variant`,
      content: (
        <div className="h-full w-full p-4">
          <PreorderPerVariantMetricCard
            variants={remainingVariants}
            baselineVariantCount={comparisonVariantIds.length}
            baselineVariantData={comparisonVariantData}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
      footer: (
        <div>
          Δ {formatName("preorder", false, true)} per variant: {ppvDelta > 0 && "+"}
          {formatNumber(ppvDelta)}
        </div>
      ),
    },
    {
      title: `${formatName("preorder", false, true)} revenue`,
      description: `The monetary value of the ${formatName("preorder", true, false)}`,
      content: (
        <div className="h-full w-full p-4">
          <RevenueTotalMetricCard
            variants={remainingVariants}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
      footer: <div>Revenue trimmed variants: {sumBy(removedVariants, (v) => v.revenueSource)}</div>,
    },
    {
      title: `Variant revenue depth`,
      description: `The average ${formatName("preorder", true, false)} revenue per variant (Total ${formatName("preorder", false, true)} revenue divided by the variant count)`,
      content: (
        <div className="h-full w-full p-4">
          <RevenuePerVariantMetricCard
            variants={remainingVariants}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
      footer: <div>Δ revenue per variant: {rpvDelta}</div>,
    },
    {
      title: columnNaming[SalesMetricColumns.FinalForecast].columnTitle,
      content: (
        <div className="h-full w-full p-4">
          <TotalForecastedDemandCard
            variants={remainingVariants}
            baselineVariantData={comparisonVariantData}
            baselineCollectionName={baselineCollectionName}
          />
        </div>
      ),
    },
  ]
  const inspector = (
    <>
      <CollectionInfoCard
        collections={sourceCollections}
        variants={variants}
        defaultExpanded={false}
      />
      <SelectableCard
        className="h-auto"
        containerClassName="p-0"
        entries={cardDataComponents}
        selectedIndex={0}
      />
      <SelectableCard
        className="h-auto"
        containerClassName="p-0"
        entries={cardDataComponents}
        selectedIndex={1}
      />
      <SelectableCard
        className="h-auto"
        containerClassName="p-0"
        entries={cardDataComponents}
        selectedIndex={2}
      />
      <SelectableCard
        className="h-auto"
        containerClassName="p-0"
        entries={cardDataComponents}
        selectedIndex={3}
      />
    </>
  )

  return (
    <WorkspacePage
      fillContent
      contentClassName="gap-y-4"
      inspector={inspector}
      viewConfiguration={
        <ViewConfiguration
          tableId={COLLECTIONS_DEMAND_TRIM_TAB_TABLE_ID}
          showFilters
          showWarehouseSelect={false}
          showChannelSelect={false}
        />
      }
    >
      {variantsLoading ? (
        <LoadingOverlay />
      ) : (
        <div className="flex flex-col h-full gap-y-4">
          <ProductTrimTable
            collectionIds={sourceCollectionIds}
            comparisonCollectionIds={baselineCollectionIds}
            variants={variants}
            sourceCollectionName={sourceCollectionName}
            baselineCollectionName={baselineCollectionName}
            trimmedVariantIds={tempTrimmedVariantIds}
            setTrimmedVariantIds={setTempTrimmedVariants}
            topRightComponent={
              <div className="w-full flex justify-end">
                <Button
                  className="flex w-fit"
                  requires="edit"
                  disabled={!unsavedChanges}
                  onClick={saveTrimChanges}
                  variant={"primary"}
                  size="sm"
                >
                  Apply Changes
                </Button>
              </div>
            }
          />
        </div>
      )}
    </WorkspacePage>
  )
}
